/**
 * The browser's image fetch for a source URL: direct → Cloudflare Worker →
 * the `proxyImage` callable, which runs the server's escalation ladder
 * (functions/src/imageFetchLadder.ts — browser headers + Referer, the Worker,
 * the public image CDN).
 *
 * Dependency-injected so the strategy order is testable without loading
 * firebase.ts; `fetchImageBlob` there is the wired-up instance.
 */

/**
 * Thrown when a fetched URL turns out not to be a usable image. Carries a
 * message worth showing the curator verbatim, unlike the generic "all
 * strategies failed".
 */
export class ReferenceImageFetchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReferenceImageFetchError';
  }
}

export interface ImageBlobFetchDeps {
  fetch: typeof fetch;
  /** The Cloudflare Worker, when configured. */
  cloudflare?: { url: string; secret: string };
  /** The `proxyImage` callable. Rejects with a FirebaseError (`code`, `message`). */
  proxyImage: (url: string) => Promise<{ base64: string; contentType: string }>;
  log?: (message: string, detail?: unknown) => void;
}

/** A declared type that is definitely not an image (an empty type is unknown, not "not"). */
export function isNonImageBlobType(type: string | undefined): boolean {
  const t = (type ?? '').split(';')[0].trim().toLowerCase();
  return t !== '' && !t.startsWith('image/') && t !== 'application/octet-stream';
}

export function base64ToBlob(base64: string, contentType: string): Blob {
  const binaryStr = atob(base64);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
}

export async function fetchImageBlobWith(url: string, deps: ImageBlobFetchDeps): Promise<Blob> {
  const log = deps.log ?? (() => undefined);

  // Strategy 1: Direct fetch (works for Shopify, own domain, etc.)
  // Cap this at 2.5s — a hung or slow-failing direct fetch is the main source
  // of per-watch stalls during bulk import. If it doesn't respond by then,
  // abort and fall through to the (fast) Cloudflare worker / Cloud Function.
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2500);
    try {
      const response = await deps.fetch(url, { mode: 'cors', signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      if (blob.size === 0) throw new Error('Empty response (likely CORS)');
      // A bot-check / error page served as 200 is not the image — try the proxies.
      if (isNonImageBlobType(blob.type)) throw new Error(`Returned ${blob.type}`);
      return blob;
    } finally {
      clearTimeout(timeout);
    }
  } catch (directErr) {
    log(`Direct fetch failed for ${url}, trying proxies...`, directErr);
  }

  // Strategy 2: Cloudflare Worker image proxy (best for CORS-blocked sites)
  if (deps.cloudflare) {
    try {
      const resp = await deps.fetch(deps.cloudflare.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Proxy-Secret': deps.cloudflare.secret,
        },
        body: JSON.stringify({ url, mode: 'image' }),
      });
      if (resp.ok) {
        const data = (await resp.json()) as { base64: string; contentType: string };
        if (data.base64 && !isNonImageBlobType(data.contentType)) {
          log(`[image] Cloudflare proxy succeeded for ${url}`);
          return base64ToBlob(data.base64, data.contentType);
        }
      }
    } catch (cfErr) {
      log(`[image] Cloudflare proxy failed for ${url}:`, cfErr);
    }
  }

  // Strategy 3: Cloud Function proxy — the server-side escalation ladder.
  try {
    const { base64, contentType } = await deps.proxyImage(url);
    return base64ToBlob(base64, contentType);
  } catch (proxyErr) {
    // When the ladder reports the shop as blocking every route, its message
    // names the host — pass it on verbatim.
    const code = (proxyErr as { code?: string })?.code;
    const detail = proxyErr instanceof Error ? proxyErr.message : '';
    if (code === 'functions/failed-precondition' && detail) {
      throw new ReferenceImageFetchError(detail);
    }
    throw new Error(`All image fetch strategies failed for ${url}${detail ? ` (${detail})` : ''}`);
  }
}
